import { AxiosError, AxiosResponse } from 'axios';
import qs from 'query-string';
import useToggle from '../../hooks/useToggle';
import React from 'react';
import useService from '../../hooks/useService';

const getNextLink = (response: AxiosResponse) => {
  const nextLink = response?.data?.links?.next;
  if (!nextLink || response?.data?.items?.length === 0) return null;

  // only grab the query params from the string
  const queryParams = nextLink?.split('?')[1];
  const { nextPageToken } = qs.parse(queryParams);

  return Array.isArray(nextPageToken) ? nextPageToken[0] : nextPageToken;
};

const getResults = (response: AxiosResponse) => {
  if (response?.data?.items) {
    return response?.data?.items;
  }
  return [];
};

interface LoadResultsResult {
  results: any[];
  nextLink: string | null;
  lastResponse?: AxiosResponse;
}

const loadResults = async (
  nextUrl: string | null,
  request: any,
  params: any,
): Promise<LoadResultsResult> => {
  const emptyResult: LoadResultsResult = {
    results: [],
    nextLink: null,
  };

  try {
    const response: AxiosResponse = await request({
      nextPageToken: nextUrl || undefined,
      ...params,
    });

    if (response?.status === 200) {
      return {
        results: getResults(response),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        nextLink: getNextLink(response),
        lastResponse: response,
      };
    } else {
      return emptyResult;
    }
  } catch (e) {
    console.error('Error loading results', e);
    return emptyResult;
  }
};

export interface UsePaginationResult {
  error?: AxiosError;
  hasMoreToLoad: boolean;
  isLoading: boolean;
  loadMore: () => Promise<void>;
  makeFetchRequest: (params?: any) => void;
  resetResults: () => void;
  results: any[];
  /**
   * @description the full response object from the last request.  Helpful
   * when meta data on the response, other than response.data.items need to
   * be determined
   */
  lastResponse?: AxiosResponse;
}

export function usePagination(axiosService: any) {
  const [cachedList, setCachedList] = React.useState([]);
  const [fetchParams, setFetchParams] = React.useState(null);
  const [isLoading, setIsLoading, setLoadingFinished] = useToggle(false);
  const [nextUrl, setNextUrl] = React.useState(null);
  const [lastResponse, setLastResponse] = React.useState(null);
  const [request, requestState] = useService(axiosService);

  const listUpdateCount = React.useRef(0);

  const makeFetchRequest = React.useCallback(async params => {
    listUpdateCount.current++;

    const updateCountBeforeLoad = listUpdateCount.current;

    setFetchParams(params);
    setIsLoading();
    const result = await loadResults(nextUrl, request, params);

    if (updateCountBeforeLoad === listUpdateCount.current) {
      setLoadingFinished();
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setCachedList(result.results);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setNextUrl(result.nextLink);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setLastResponse(result.lastResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // downstream useEffects get triggered by this changing.  TODO: refactor downstream useEffects

  const handleLoadMore = React.useCallback(async () => {
    if (!nextUrl) return;

    const updateCountBeforeLoad = listUpdateCount.current;

    setIsLoading();
    const result = await loadResults(nextUrl, request, fetchParams);

    if (updateCountBeforeLoad === listUpdateCount.current) {
      setLoadingFinished();
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setCachedList([...cachedList, ...result.results]);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setNextUrl(result.nextLink);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setLastResponse(result.lastResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchParams, nextUrl, cachedList]); // downstream useEffects get triggered by this changing.  TODO: refactor downstream useEffects

  const handleResetResults = () => {
    setLoadingFinished();
    setCachedList([]);
    setFetchParams(null);
    setNextUrl(null);
    setLastResponse(null);
  };

  const returnValue: UsePaginationResult = {
    hasMoreToLoad: !!nextUrl,
    isLoading,
    loadMore: handleLoadMore,
    makeFetchRequest,
    resetResults: handleResetResults,
    results: cachedList,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    lastResponse,
    error: requestState?.error,
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => returnValue, [...Object.values(returnValue)]);
}
